import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Search, Download, RefreshCw, Filter, X, Package, Calendar, CheckCircle2, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const BundleReceiptReport = () => {
  const navigate = useNavigate();
  const { section: sectionSlug } = useParams();
  const [bundles, setBundles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [totalQty, setTotalQty] = useState(0);
  
  // Filters
  const [filters, setFilters] = useState({
    bundle_code: '',
    design_name: '',
    date_from: '',
    date_to: ''
  });
  const [showFilters, setShowFilters] = useState(true);

  const fetchBundles = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.bundle_code) params.append('bundle_code', filters.bundle_code);
      if (filters.design_name) params.append('design_name', filters.design_name);
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);
      if (sectionSlug) params.append('section', sectionSlug);
      params.append('limit', '500');
      
      const response = await apiClient.get(`/sections/dispatch/bundle-receipt-report?${params.toString()}`);
      
      if (response.data.success) {
        setBundles(response.data.bundles || []);
        setTotalCount(response.data.total || 0);
        setTotalQty(response.data.total_qty || 0);
      }
    } catch (error) {
      console.error('Error fetching bundles:', error);
      toast.error('Failed to load bundle receipt report');
    } finally {
      setIsLoading(false);
    }
  }, [filters, sectionSlug]);

  useEffect(() => {
    fetchBundles();
  }, [sectionSlug]);  // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = () => {
    fetchBundles();
  };

  const handleClearFilters = () => {
    setFilters({ bundle_code: '', design_name: '', date_from: '', date_to: '' });
  };

  const handleExportCSV = () => {
    if (bundles.length === 0) {
      toast.error('No data to export');
      return;
    }
    
    const headers = ['Bundle Number', 'Barcode', 'Design Name', 'Shade', 'Size', 'Description', 'Qty', 'Received At', 'Label Printed', 'Put Away Slip'];
    const csvContent = [
      headers.join(','),
      ...bundles.map(item => [
        item.bundle_number,
        item.barcode,
        `"${item.design_name}"`,
        item.shade,
        item.size,
        `"${item.description || ''}"`,
        item.qty,
        item.received_at,
        item.label_printed ? 'Yes' : 'No',
        item.fg_put_away_slip_id || '-'
      ].join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `bundle_receipt_report_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success('Report exported successfully');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleString();
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center">
            <Button
              variant="ghost"
              onClick={() => navigate(-1)}
              className="text-[#F9FAFB] hover:bg-white/10 mr-4"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display">Bundle Receipt Report</h1>
              <p className="text-sm text-[#E8DCC8]">Dispatch Section - Received Bundles</p>
            </div>
          </div>
          <Button
            variant="outline"
            onClick={handleExportCSV}
            className="bg-white/10 border-white/20 text-white hover:bg-white/20"
            data-testid="export-csv-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Package className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Bundles</p>
                <p className="text-2xl font-bold">{totalCount}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="p-3 bg-green-100 rounded-lg">
                <CheckCircle2 className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Qty (USNs)</p>
                <p className="text-2xl font-bold">{totalQty}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="p-3 bg-purple-100 rounded-lg">
                <Calendar className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Showing</p>
                <p className="text-2xl font-bold">{bundles.length}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardHeader className="py-3 px-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Filter className="w-5 h-5" />
                Filters
              </CardTitle>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowFilters(!showFilters)}
                >
                  {showFilters ? 'Hide' : 'Show'}
                </Button>
                <Button
                  onClick={handleSearch}
                  size="sm"
                  className="bg-[#2D5F4F] hover:bg-[#234A3D]"
                  disabled={isLoading}
                  data-testid="search-btn"
                >
                  {isLoading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Search className="w-4 h-4 mr-2" />}
                  Search
                </Button>
              </div>
            </div>
          </CardHeader>
          {showFilters && (
            <CardContent className="pt-0 pb-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Bundle Number</label>
                  <Input
                    value={filters.bundle_code}
                    onChange={(e) => setFilters(prev => ({ ...prev, bundle_code: e.target.value }))}
                    placeholder="BUN..."
                    data-testid="filter-bundle-code"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Design Name</label>
                  <Input
                    value={filters.design_name}
                    onChange={(e) => setFilters(prev => ({ ...prev, design_name: e.target.value }))}
                    placeholder="Omkar"
                    data-testid="filter-design"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Date From</label>
                  <Input
                    type="date"
                    value={filters.date_from}
                    onChange={(e) => setFilters(prev => ({ ...prev, date_from: e.target.value }))}
                    data-testid="filter-date-from"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Date To</label>
                  <Input
                    type="date"
                    value={filters.date_to}
                    onChange={(e) => setFilters(prev => ({ ...prev, date_to: e.target.value }))}
                    data-testid="filter-date-to"
                  />
                </div>
              </div>
              <div className="mt-3 flex justify-end">
                <Button variant="ghost" size="sm" onClick={handleClearFilters}>
                  <X className="w-4 h-4 mr-2" />
                  Clear Filters
                </Button>
              </div>
            </CardContent>
          )}
        </Card>

        {/* Bundles Table */}
        <Card>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
              </div>
            ) : bundles.length === 0 ? (
              <div className="text-center py-12">
                <Package className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500">No bundle receipts found</p>
                <p className="text-sm text-gray-400">Try adjusting your filters</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="font-semibold">Bundle Number</TableHead>
                      <TableHead className="font-semibold">Barcode</TableHead>
                      <TableHead className="font-semibold">Design Name</TableHead>
                      <TableHead className="font-semibold">Shade</TableHead>
                      <TableHead className="font-semibold">Size</TableHead>
                      <TableHead className="font-semibold">Description</TableHead>
                      <TableHead className="font-semibold text-center">Qty</TableHead>
                      <TableHead className="font-semibold">Received At</TableHead>
                      <TableHead className="font-semibold text-center">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {bundles.map((bundle, index) => (
                      <TableRow key={`${bundle.bundle_number}-${index}`} className="hover:bg-gray-50">
                        <TableCell className="font-mono font-bold text-purple-700">{bundle.bundle_number}</TableCell>
                        <TableCell className="font-mono text-sm">{bundle.barcode || '-'}</TableCell>
                        <TableCell className="font-medium">{bundle.design_name}</TableCell>
                        <TableCell>{bundle.shade || '-'}</TableCell>
                        <TableCell>{bundle.size || '-'}</TableCell>
                        <TableCell className="max-w-xs truncate" title={bundle.description}>
                          {bundle.description || '-'}
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge className="bg-blue-100 text-blue-800">{bundle.qty}</Badge>
                        </TableCell>
                        <TableCell className="text-sm text-gray-600">
                          {formatDate(bundle.received_at)}
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex flex-col items-center gap-1">
                            {bundle.label_printed ? (
                              <Badge className="bg-green-100 text-green-800 text-xs">
                                <CheckCircle2 className="w-3 h-3 mr-1" />
                                Label Printed
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-xs">
                                <Clock className="w-3 h-3 mr-1" />
                                Pending Label
                              </Badge>
                            )}
                            {bundle.fg_put_away_slip_id && (
                              <Badge className="bg-purple-100 text-purple-800 text-xs font-mono">
                                {bundle.fg_put_away_slip_id}
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default BundleReceiptReport;
